export type ProjectCategory = 'AI + ML' | 'Hardware' | 'Tools' | 'Experiments'
export type ProjectVisibility = 'featured' | 'standard' | 'supporting' | 'collection-member' | 'hidden'

export type ProjectDestination = {
  label: string
  url: string
  type: 'live' | 'docs' | 'source'
}

export type Project = {
  name: string
  description: string
  html_url: string
  homepage: string | null
  language: string | null
  stargazers_count: number
  forks_count: number
  topics: string[]
  updated_at: string
  fork: boolean
  category: ProjectCategory
  visibility: ProjectVisibility
  feature?: boolean
  collection?: string
  portfolioNote?: string
  destinations?: ProjectDestination[]
}

export const featuredProjectName = 'intel-arc-pro-b70-benchmarks'

export const curatedProjects: Project[] = [
  {
    name: 'mod-homebrew-gm',
    description: 'A GM administration UI and RBAC-protected server bridge for AzerothCore and mod-playerbots.',
    html_url: 'https://github.com/PMZFX/mod-homebrew-gm',
    homepage: null,
    language: 'Lua',
    stargazers_count: 0,
    forks_count: 0,
    topics: ['addon', 'azerothcore', 'gm-tools', 'mod-playerbots', 'playerbots', 'wotlk', 'wow'],
    updated_at: '2026-08-08T15:45:08Z',
    fork: false,
    category: 'Tools',
    collection: 'azerothcore-playerbots',
    visibility: 'collection-member',
  },
  {
    name: 'mod-homebrew-nourishment',
    description:
      'Completed-meal buffs, persistent server-side effects, and accurate client tooltips for AzerothCore food and drink.',
    html_url: 'https://github.com/PMZFX/mod-homebrew-nourishment',
    homepage: null,
    language: 'C++',
    stargazers_count: 0,
    forks_count: 0,
    topics: ['addon', 'azerothcore', 'food-and-drink', 'mod-playerbots', 'playerbots', 'wotlk', 'wow'],
    updated_at: '2026-08-08T18:55:03Z',
    fork: false,
    category: 'Tools',
    collection: 'azerothcore-playerbots',
    visibility: 'collection-member',
  },
  {
    name: 'mod-homebrew-vendors',
    description:
      'A deterministic 28-day rotation of useful, themed, level-appropriate stock for AzerothCore merchants, with optional Playerbot protection.',
    html_url: 'https://github.com/PMZFX/mod-homebrew-vendors',
    homepage: null,
    language: 'Python',
    stargazers_count: 0,
    forks_count: 0,
    topics: ['addon', 'azerothcore', 'mod-playerbots', 'playerbots', 'vendors', 'wotlk', 'wow'],
    updated_at: '2026-08-09T10:12:51Z',
    fork: false,
    category: 'Tools',
    collection: 'azerothcore-playerbots',
    visibility: 'collection-member',
  },
  {
    name: 'intel-arc-pro-b70-benchmarks',
    description:
      'Real-world LLM inference, video generation, and dual-GPU scaling data for Intel Arc Pro B70 hardware.',
    html_url: 'https://github.com/PMZFX/intel-arc-pro-b70-benchmarks',
    homepage: null,
    language: null,
    stargazers_count: 80,
    forks_count: 7,
    topics: ['gpu-benchmarks', 'llm-inference', 'sycl', 'intel-arc'],
    updated_at: '2026-08-07T14:15:45Z',
    fork: false,
    category: 'Hardware',
    feature: true,
    visibility: 'featured',
  },
  {
    name: 'whats-for-dinner',
    description: 'An easy, accessible dinner picker for choosing what to cook.',
    html_url: 'https://github.com/PMZFX/whats-for-dinner',
    homepage: 'https://whats-for-dinner-b1w.pages.dev/',
    language: 'CSS',
    stargazers_count: 0,
    forks_count: 0,
    topics: [],
    updated_at: '2026-08-05T20:28:10Z',
    fork: false,
    category: 'Tools',
    visibility: 'standard',
    destinations: [
      {
        label: 'Pick dinner',
        url: 'https://whats-for-dinner-b1w.pages.dev/',
        type: 'live',
      },
    ],
  },
  {
    name: 'zoom-o-meter',
    description:
      'A kid-friendly GPS speedometer with practical and whimsical units, offline support, and tested conversion logic.',
    html_url: 'https://github.com/PMZFX/zoom-o-meter',
    homepage: 'https://techmakesart.com/speedometer/',
    language: 'JavaScript',
    stargazers_count: 0,
    forks_count: 0,
    topics: ['gps', 'javascript', 'offline-first', 'pwa', 'speedometer', 'unit-converter'],
    updated_at: '2026-08-08T20:31:00Z',
    fork: false,
    category: 'Tools',
    visibility: 'standard',
    destinations: [
      {
        label: 'Open app',
        url: 'https://techmakesart.com/speedometer/',
        type: 'live',
      },
    ],
  },
  {
    name: 'world-of-claudecraft',
    description: 'A lightly modified fork used to explore the project and remove some telemetry.',
    html_url: 'https://github.com/PMZFX/world-of-claudecraft',
    homepage: 'https://worldofclaudecraft.com',
    language: 'TypeScript',
    stargazers_count: 0,
    forks_count: 0,
    topics: [],
    updated_at: '2026-06-23T19:28:22Z',
    fork: true,
    category: 'Experiments',
    portfolioNote: 'Reference fork — limited original work',
    visibility: 'supporting',
  },
  {
    name: 'llama.cpp-sycl',
    description: 'A working fork of llama.cpp focused on LLM inference through Intel SYCL.',
    html_url: 'https://github.com/PMZFX/llama.cpp-sycl',
    homepage: null,
    language: 'C++',
    stargazers_count: 0,
    forks_count: 0,
    topics: [],
    updated_at: '2026-06-09T10:03:33Z',
    fork: true,
    category: 'AI + ML',
    visibility: 'standard',
  },
  {
    name: 'TT-qwen35moe-blackhole-p100-slim-gate-up-fused',
    description: 'Exploring fused gate/up projection paths for Qwen MoE on Tenstorrent Blackhole.',
    html_url: 'https://github.com/PMZFX/TT-qwen35moe-blackhole-p100-slim-gate-up-fused',
    homepage: null,
    language: 'Python',
    stargazers_count: 0,
    forks_count: 0,
    topics: [],
    updated_at: '2026-05-24T23:17:58Z',
    fork: false,
    category: 'Hardware',
    collection: 'tenstorrent-blackhole',
    visibility: 'collection-member',
  },
  {
    name: 'TT-qwen36-35b-a3b-fp8-two-blackhole',
    description: 'Getting Qwen 3.6 running across two Tenstorrent Blackhole cards.',
    html_url: 'https://github.com/PMZFX/TT-qwen36-35b-a3b-fp8-two-blackhole',
    homepage: null,
    language: 'Python',
    stargazers_count: 0,
    forks_count: 0,
    topics: [],
    updated_at: '2026-05-19T19:28:32Z',
    fork: false,
    category: 'Hardware',
    collection: 'tenstorrent-blackhole',
    visibility: 'collection-member',
  },
  {
    name: 'speecht5-voice-conversion',
    description: 'SpeechT5 voice conversion ported to the Tenstorrent TTNN API.',
    html_url: 'https://github.com/PMZFX/speecht5-voice-conversion',
    homepage: null,
    language: 'Python',
    stargazers_count: 0,
    forks_count: 0,
    topics: [],
    updated_at: '2026-04-15T11:20:52Z',
    fork: false,
    category: 'Hardware',
    collection: 'tenstorrent-blackhole',
    visibility: 'collection-member',
  },
]

const categoryHints: Record<ProjectCategory, string[]> = {
  'AI + ML': ['llm', 'qwen', 'speech', 'voice', 'model'],
  Hardware: ['gpu', 'intel', 'arc', 'sycl', 'blackhole', 'tenstorrent', 'tt-'],
  Tools: ['tool', 'picker', 'dinner', 'utility'],
  Experiments: ['world', 'game', 'experiment'],
}

export function categorizeProject(project: Pick<Project, 'name' | 'description' | 'topics'>): ProjectCategory {
  const haystack = `${project.name} ${project.description} ${project.topics.join(' ')}`.toLowerCase()

  for (const category of ['Tools', 'Experiments', 'Hardware', 'AI + ML'] as ProjectCategory[]) {
    if (categoryHints[category].some((hint) => haystack.includes(hint))) return category
  }

  return 'Experiments'
}
